import React, { useEffect, useRef, useState } from "react";
import "./PartnersPage.scss";
import "./../ContactSection/ContactSection.scss";

import Input from "../Input/Input";
import FetchService from "../../api/FetchService";

// ===============================
// RECAPTCHA CONSTANTS
// ===============================

const RECAPTCHA_SITE_KEY = "6Ldqj8stAAAAACfoN-g7TCFlTZh0vM69bkD4h6m1";

let recaptchaLoaderPromise = null;

/**
 * Loads the Google reCAPTCHA v2 script once and resolves when it is available.
 */
const loadRecaptcha = () => {
    if (typeof window === "undefined") {
        return Promise.resolve(false);
    }

    if (window.grecaptcha && window.grecaptcha.render) {
        return Promise.resolve(true);
    }

    if (recaptchaLoaderPromise) {
        return recaptchaLoaderPromise;
    }

    recaptchaLoaderPromise = new Promise((resolve) => {
        let settled = false;

        const finish = (loaded) => {
            if (settled) {
                return;
            }

            settled = true;
            resolve(loaded);
        };

        const existingScript = document.querySelector(
            'script[data-funtlers-recaptcha="true"]'
        );

        if (existingScript) {
            existingScript.addEventListener("load", () => finish(true));
            existingScript.addEventListener("error", () => finish(false));
        } else {
            const script = document.createElement("script");

            script.src = "https://www.google.com/recaptcha/api.js";
            script.async = true;
            script.defer = true;
            script.setAttribute("data-funtlers-recaptcha", "true");
            script.addEventListener("load", () => finish(true));
            script.addEventListener("error", () => finish(false));

            document.head.appendChild(script);
        }

        setTimeout(
            () =>
                finish(
                    Boolean(window.grecaptcha && window.grecaptcha.render)
                ),
            10000
        );
    });

    return recaptchaLoaderPromise;
};

function PartnersPage() {
    // ===============================
    // FORM STATE
    // ===============================

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [reason, setReason] = useState("");

    // Honeypot field
    const [honeypot, setHoneypot] = useState("");

    // Form status
    const [isSending, setIsSending] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isVerifyingCaptcha, setIsVerifyingCaptcha] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Keeps track of the auto hide timer of the success message
    const successTimerRef = useRef(null);
    const recaptchaWidgetRef = useRef(null);

    // ===============================
    // CONSTANTS
    // ===============================

    const MIN_MESSAGE_LENGTH = 10;
    const MAX_MESSAGE_LENGTH = 5000;
    const MAX_URLS_ALLOWED = 2;

    // ===============================
    // CLEAR ERROR ON INPUT / UNMOUNT
    // ===============================

    useEffect(() => {
        setErrorMessage("");
    }, [name, email, message, reason]);

    useEffect(() => {
        const renderCaptcha = async () => {
            const isLoaded = await loadRecaptcha();

            if (!isLoaded || !window.grecaptcha || !recaptchaWidgetRef.current) {
                return;
            }

            if (recaptchaWidgetRef.current.dataset.widgetId) {
                return;
            }

            const widgetId = window.grecaptcha.render(
                recaptchaWidgetRef.current,
                {
                    sitekey: RECAPTCHA_SITE_KEY,
                    callback: () => {
                        setErrorMessage("");
                    },
                    "expired-callback": () => {
                        setErrorMessage(
                            "reCAPTCHA-expirasjonen gikk ut. Vennligst bekreft igjen."
                        );
                    }
                }
            );

            recaptchaWidgetRef.current.dataset.widgetId = widgetId;
        };

        renderCaptcha();
    }, []);

    useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                clearTimeout(successTimerRef.current);
            }
        };
    }, []);

    // ===============================
    // EMAIL FORMAT VALIDATION
    // ===============================

    const validateEmail = (value) => {
        if (!value) return false;

        const emailValue = value.trim();

        if (emailValue.length > 254) {
            return false;
        }

        const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

        return emailRegex.test(emailValue);
    };

    // ===============================
    // EMAIL DOMAIN DNS VALIDATION
    // ===============================

    const dnsLookup = async (name, type) => {
        const response = await fetch(
            `https://dns.google/resolve?name=${encodeURIComponent(
                name
            )}&type=${type}`
        );

        if (!response.ok) {
            return null;
        }

        const data = await response.json();

        return data && typeof data.Status === "number" ? data : null;
    };

    // Resolves true when the domain appears to be able to receive e-mail.
    // DNS problems are treated as "unknown" so that an outage of the DNS API
    // never blocks legitimate users.
    const checkEmailDomain = async (emailValue) => {
        const domain = emailValue.split("@")[1]?.toLowerCase().trim();

        if (!domain) {
            return false;
        }

        try {
            const mxResponse = await dnsLookup(domain, "MX");

            // The DNS API is unreachable - do not block the user
            if (!mxResponse) {
                return true;
            }

            // Status 3 means the domain does not exist (NXDOMAIN)
            if (mxResponse.Status === 3) {
                return false;
            }

            if (mxResponse.Status !== 0) {
                console.warn(
                    `DNS lookup for ${domain} returned status ${mxResponse.Status}`
                );

                return true;
            }

            const answers = Array.isArray(mxResponse.Answer)
                ? mxResponse.Answer
                : [];

            const mxRecords = answers.filter(
                (record) => record?.type === 15
            );

            if (mxRecords.length > 0) {
                // A "Null MX" record (pointing at ".") means the domain
                // explicitly refuses to receive e-mail.
                return mxRecords.some(
                    (record) =>
                        typeof record.data === "string" &&
                        record.data.trim() !== "."
                );
            }

            // Without MX records, RFC 5321 still allows delivery to the
            // address record of the domain.
            const addressResponse = await dnsLookup(domain, "A");

            if (!addressResponse || addressResponse.Status !== 0) {
                return true;
            }

            const addressRecords = Array.isArray(addressResponse.Answer)
                ? addressResponse.Answer.filter(
                      (record) => record?.type === 1
                  )
                : [];

            return addressRecords.length > 0;
        } catch (error) {
            console.warn("Email domain check failed:", error);

            return true;
        }
    };

    // ===============================
    // RECAPTCHA TOKEN
    // ===============================

    const getRecaptchaToken = async () => {
        const isLoaded = await loadRecaptcha();

        if (!isLoaded || !window.grecaptcha || !recaptchaWidgetRef.current) {
            throw new Error("The reCAPTCHA widget could not be loaded.");
        }

        const token = window.grecaptcha.getResponse();

        if (!token || typeof token !== "string" || token.length === 0) {
            throw new Error("Please complete the reCAPTCHA challenge.");
        }

        return token;
    };

    // ===============================
    // TEXT VALIDATION
    // ===============================

    const containsSuspiciousPattern = (text) => {
        if (!text) return false;

        const value = text.trim();

        // Detect excessive repeated characters
        if (/(.)\1{8,}/i.test(value)) {
            return true;
        }

        // Detect long alphabetic strings without vowels
        const words = value.split(/\s+/);

        const suspiciousWords = words.filter((word) => {
            const cleanWord = word.replace(/[^a-zA-Z]/g, "");

            if (cleanWord.length < 10) {
                return false;
            }

            return (
                !/[aeiou]/i.test(cleanWord) &&
                /^[a-zA-Z]+$/.test(cleanWord)
            );
        });

        return suspiciousWords.length >= 2;
    };

    // ===============================
    // URL VALIDATION
    // ===============================

    const containsTooManyLinks = (text) => {
        if (!text) return false;

        const urlRegex =
            /(https?:\/\/|www\.)[^\s]+/gi;

        const urls = text.match(urlRegex) || [];

        return urls.length > MAX_URLS_ALLOWED;
    };

    // ===============================
    // HONEYPOT VALIDATION
    // ===============================

    const isHoneypotTriggered = () => {
        return honeypot && honeypot.trim() !== "";
    };

    // ===============================
    // FORM VALIDATION
    // ===============================

    const basicContentCheck = () => {
        // Honeypot
        if (isHoneypotTriggered()) {
            return {
                ok: false,
                reason: "Spam detected."
            };
        }

        // Required fields
        if (
            !reason?.trim() ||
            !name?.trim() ||
            !message?.trim() ||
            !email?.trim()
        ) {
            return {
                ok: false,
                reason: "Vennligst fyll ut alle feltene."
            };
        }

        // Contact person validation
        if (reason.trim().length < 2) {
            return {
                ok: false,
                reason: "Vennligst oppgi en gyldig kontaktperson."
            };
        }

        // Company name validation
        if (name.trim().length < 2) {
            return {
                ok: false,
                reason: "Vennligst oppgi et gyldig bedriftsnavn."
            };
        }

        // Email format validation
        if (!validateEmail(email)) {
            return {
                ok: false,
                reason: "Vennligst oppgi en gyldig e-postadresse."
            };
        }

        // Message minimum length
        if (message.trim().length < MIN_MESSAGE_LENGTH) {
            return {
                ok: false,
                reason: `Kommentar må være minst ${MIN_MESSAGE_LENGTH} tegn.`
            };
        }

        // Message maximum length
        if (message.trim().length > MAX_MESSAGE_LENGTH) {
            return {
                ok: false,
                reason: "Kommentar er for lang."
            };
        }

        // Excessive links
        if (containsTooManyLinks(message)) {
            return {
                ok: false,
                reason: "Meldingen inneholder for mange lenker."
            };
        }

        // Suspicious patterns
        if (
            containsSuspiciousPattern(name) ||
            containsSuspiciousPattern(reason) ||
            containsSuspiciousPattern(message)
        ) {
            return {
                ok: false,
                reason: "Meldingen inneholder mistenkelig innhold."
            };
        }

        return {
            ok: true
        };
    };

    // ===============================
    // HANDLE SUBMIT
    // ===============================

    const handleSubmit = async (e) => {
        e.preventDefault();

        setErrorMessage("");
        setSuccessMessage("");

        // Prevent duplicate submissions
        if (isSending || isCheckingEmail || isVerifyingCaptcha) {
            return;
        }

        // Basic form validation
        const check = basicContentCheck();

        if (!check.ok) {
            // Silently ignore honeypot submissions
            if (check.reason === "Spam detected.") {
                console.warn(
                    "Honeypot triggered - ignoring submit."
                );

                return;
            }

            setErrorMessage(check.reason);
            return;
        }

        const emailValue = email.trim();

        // ===============================
        // CHECK EMAIL DOMAIN
        // ===============================

        setIsCheckingEmail(true);

        const domainExists = await checkEmailDomain(emailValue);

        setIsCheckingEmail(false);

        if (!domainExists) {
            setErrorMessage(
                "E-postdomenet ser ikke ut til å kunne motta e-post. Kontroller e-postadressen."
            );

            return;
        }

        // ===============================
        // VERIFY RECAPTCHA
        // ===============================

        setIsVerifyingCaptcha(true);

        let recaptchaToken;

        try {
            recaptchaToken = await getRecaptchaToken();
        } catch (error) {
            console.warn("reCAPTCHA verification failed:", error);

            setErrorMessage(
                "Du må bekrefte at du ikke er en robot før du sender skjemaet."
            );

            setIsVerifyingCaptcha(false);
            return;
        }

        setIsVerifyingCaptcha(false);
        setIsSending(true);

        // ===============================
        // PREPARE REQUEST
        // ===============================

        const request = {
            company: name.trim(),
            email: emailValue,
            body: message.trim(),
            subject: reason.trim(),

            // Honeypot - validated on the server as well
            website: honeypot,

            // reCAPTCHA v3 token - validated on the server as well
            recaptchaToken: recaptchaToken
        };

        try {
            const response =
                await FetchService.SendEmailBecomePartner(
                    request
                );

            // The API returns a boolean: true when the e-mail was sent,
            // false when the request was rejected (spam / captcha / ...)
            if (response?.data === true) {
                setSuccessMessage(
                    "Takk for at du kontaktet oss. Vi tar kontakt snart."
                );

                // Reset form
                setName("");
                setEmail("");
                setMessage("");
                setReason("");
                setHoneypot("");

                // Clear success message
                if (successTimerRef.current) {
                    clearTimeout(successTimerRef.current);
                }

                successTimerRef.current = setTimeout(() => {
                    setSuccessMessage("");
                    successTimerRef.current = null;
                }, 10000);
            } else if (response?.data === false) {
                setErrorMessage(
                    "Forespørselen ble avvist. Vennligst prøv igjen."
                );
            } else {
                setErrorMessage(
                    "Feil. Vær så snill, prøv på nytt."
                );
            }
        } catch (err) {
            console.error(
                "SendEmailBecomePartner failed",
                err
            );

            setErrorMessage(
                "Noe gikk galt. Vennligst prøv igjen senere."
            );
        } finally {
            setIsSending(false);
        }
    };

    // ===============================
    // RENDER
    // ===============================

    return (
        <div
            data-testid="wrapper"
            className="_loading_overlay_wrapper css-79elbk Contact_main"
        >
            <div className="searchRsult_page">
                <div className="container">
                    <div className="inner_header_main">
                        <div className="header_wrapper">
                            VIL DU BLI VÅR SAMARBEIDSPARTNER?
                        </div>
                    </div>
                </div>

                <div className="searchRsult_main">
                    <div className="container">
                        <div className="wrapper">
                            <div className="row row_custom">
                                {/* LEFT CONTENT */}

                                <div className="col-lg-6 col_content_otr partner-wrapper">
                                    <p className="heading filter_heading">
                                        Funtlers er på god vei til å bli en ledende sosial markedsplass i Norge, og vi tilbyr både fysiske og digitale aktiviteter.

                                        Ved å samarbeide med oss får du som aktivitetsleverandør tilgang til flere spennende muligheter:
                                    </p>

                                    <ul className="benefits-list">
                                        <li>
                                            <p>
                                                <strong>
                                                    Unik eksponering:
                                                </strong>{" "}
                                                Øk synligheten til virksomheten din blant et bredt publikum.
                                            </p>
                                        </li>

                                        <li>
                                            <p>
                                                <strong>
                                                    Flere bookinger:
                                                </strong>{" "}
                                                Maksimer antall bookinger månedlig gjennom vårt nettverk.
                                            </p>
                                        </li>

                                        <li>
                                            <p>
                                                <strong>
                                                    Eksklusivt utvalg:
                                                </strong>{" "}
                                                Vær en del av vårt unike tilbud av sosiale aktiviteter.
                                            </p>
                                        </li>

                                        <li>
                                            <p>
                                                <strong>
                                                    Trofaste kunder:
                                                </strong>{" "}
                                                Tiltrekk deg tilbakevendende kunder som verdsetter kvalitet og fellesskap.
                                            </p>
                                        </li>
                                    </ul>

                                    <p className="heading filter_heading">
                                        Er du interessert? Fyll ut skjemaet, så tar vi kontakt for en uforpliktende samtale.
                                    </p>
                                </div>

                                {/* RIGHT FORM */}

                                <div className="col-lg-6 col_form_otr">
                                    <form
                                        className="col_form_inr col_form_inr-cnt"
                                        onSubmit={handleSubmit}
                                        noValidate
                                    >
                                        {/* HONEYPOT */}

                                        <input
                                            type="text"
                                            name="website"
                                            value={honeypot}
                                            onChange={(e) =>
                                                setHoneypot(
                                                    e.target.value
                                                )
                                            }
                                            autoComplete="off"
                                            tabIndex={-1}
                                            aria-hidden="true"
                                            style={{
                                                position: "absolute",
                                                left: "-9999px",
                                                width: "1px",
                                                height: "1px",
                                                opacity: 0,
                                                pointerEvents: "none"
                                            }}
                                        />

                                        {/* CONTACT PERSON */}

                                        <div className="input_otr">
                                            <Input
                                                InputClass="Theme_input_white"
                                                Inputype="text"
                                                InputName="reason"
                                                InputPlaceholder="Kontaktperson"
                                                value={reason}
                                                onChange={(event) =>
                                                    setReason(
                                                        event.target.value
                                                    )
                                                }
                                                maxLength={100}
                                                required
                                            />
                                        </div>

                                        {/* COMPANY NAME */}

                                        <div className="input_otr">
                                            <Input
                                                InputClass="Theme_input_white"
                                                Inputype="text"
                                                InputName="name"
                                                InputPlaceholder="Bedriftsnavn"
                                                value={name}
                                                onChange={(event) =>
                                                    setName(
                                                        event.target.value
                                                    )
                                                }
                                                maxLength={150}
                                                required
                                            />
                                        </div>

                                        {/* EMAIL */}

                                        <div className="input_otr">
                                            <Input
                                                InputClass="Theme_input_white"
                                                Inputype="email"
                                                InputName="email"
                                                InputPlaceholder="E-post"
                                                value={email}
                                                onChange={(event) =>
                                                    setEmail(
                                                        event.target.value
                                                    )
                                                }
                                                maxLength={254}
                                                required
                                            />
                                        </div>

                                        {/* MESSAGE */}

                                        <div className="input_otr">
                                            <textarea
                                                className="Theme_input_white Text_area"
                                                name="message"
                                                placeholder="Kommentar"
                                                value={message}
                                                onChange={(event) =>
                                                    setMessage(
                                                        event.target.value
                                                    )
                                                }
                                                maxLength={MAX_MESSAGE_LENGTH}
                                                rows={6}
                                                required
                                            />
                                        </div>

                                        {/* reCAPTCHA */}

                                        <div className="mb-3">
                                            <div
                                                ref={recaptchaWidgetRef}
                                                className="g-recaptcha"
                                                data-sitekey={RECAPTCHA_SITE_KEY}
                                            />
                                        </div>

                                        {/* ERROR MESSAGE */}

                                        {errorMessage && (
                                            <div
                                                className="error_message mb-2"
                                                role="alert"
                                            >
                                                {errorMessage}
                                            </div>
                                        )}

                                        {/* SUCCESS MESSAGE */}

                                        {successMessage && (
                                            <div
                                                className="success_message mb-2"
                                                role="status"
                                            >
                                                {successMessage}
                                            </div>
                                        )}

                                        {/* SUBMIT BUTTON */}

                                        <div className="action_otr">
                                            <button
                                                type="submit"
                                                className="Theme_btn_primary send_btn"
                                                disabled={
                                                    isSending ||
                                                    isCheckingEmail ||
                                                    isVerifyingCaptcha
                                                }
                                                aria-busy={
                                                    isSending ||
                                                    isCheckingEmail ||
                                                    isVerifyingCaptcha
                                                }
                                                aria-disabled={
                                                    isSending ||
                                                    isCheckingEmail ||
                                                    isVerifyingCaptcha
                                                }
                                            >
                                                {isCheckingEmail
                                                    ? "Kontrollerer e-post..."
                                                    : isVerifyingCaptcha
                                                        ? "Verifiserer..."
                                                        : isSending
                                                            ? "Sender..."
                                                            : "Send"}
                                            </button>
                                        </div>

                                        {/* RECAPTCHA NOTICE */}

                                  
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PartnersPage;